import React from 'react'
import CosmicNebulaMastercard from '../components/ui/cursor-wander-card'

const CursorWanderCardDemoPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Page Header */}
      <div className="py-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-white mb-6">
            🌌 Cosmic Nebula Cards
          </h1>
          <p className="text-xl text-white/80 leading-relaxed mb-8">
            Interactive 3D credit cards with cosmic animations, particle systems, and mouse-tracking effects. 
            Perfect for financial dashboards, premium membership cards, or futuristic UI designs.
          </p>
        </div>
      </div>

      {/* Main Demo Section */}
      <div className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          
          {/* Featured Card */}
          <div className="text-center mb-24">
            <h2 className="text-3xl font-bold text-white mb-4">✨ Interactive 3D Experience</h2>
            <p className="text-white/60 mb-12">Hover over the card to control its rotation and see the cosmic effects come to life</p>
            
            <div className="flex justify-center">
              <CosmicNebulaMastercard 
                cardholderName="HUDSON CLUB MEMBER"
                logoText={{ topText: "HUDSON", bottomText: "CLUB" }}
                theme={{
                  primaryColor: "#0FA0CE",
                  secondaryColor: "#0056b3",
                  glowColor: "rgba(15, 160, 206, 0.8)"
                }}
              />
            </div>
          </div>

          {/* Card Variations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-12 mb-24">
            
            {/* Hudson Club Premium */}
            <div className="text-center">
              <h3 className="text-xl font-semibold text-white mb-4">🏠 Hudson Club Premium</h3>
              <CosmicNebulaMastercard 
                cardholderName="BONNIE LEPAGE"
                logoText={{ topText: "HUDSON", bottomText: "PREMIUM" }}
                theme={{
                  primaryColor: "#82764d",
                  secondaryColor: "#1e1d1c",
                  glowColor: "rgba(130, 118, 77, 0.8)"
                }}
                width="350px"
                height="220px"
              />
              <p className="text-white/60 text-sm mt-4">Hudson Club branded with signature colors</p>
            </div>

            {/* Emerald Luxury */}
            <div className="text-center">
              <h3 className="text-xl font-semibold text-white mb-4">💎 Emerald Luxury</h3>
              <CosmicNebulaMastercard 
                cardholderName="VIP MEMBER"
                logoText={{ topText: "EMERALD", bottomText: "ELITE" }}
                theme={{
                  primaryColor: "#10b981",
                  secondaryColor: "#059669",
                  glowColor: "rgba(16, 185, 129, 0.8)"
                }}
                width="350px"
                height="220px"
              />
              <p className="text-white/60 text-sm mt-4">Luxury emerald theme with premium effects</p>
            </div>

            {/* Golden Prestige */}
            <div className="text-center">
              <h3 className="text-xl font-semibold text-white mb-4">👑 Golden Prestige</h3>
              <CosmicNebulaMastercard 
                cardholderName="PRESTIGE HOLDER"
                logoText={{ topText: "GOLD", bottomText: "CARD" }}
                theme={{
                  primaryColor: "#f59e0b",
                  secondaryColor: "#d97706",
                  glowColor: "rgba(245, 158, 11, 0.8)"
                }}
                width="350px"
                height="220px"
              />
              <p className="text-white/60 text-sm mt-4">Golden prestige with warm cosmic glow</p>
            </div>
          </div>

          {/* Compact Variations */}
          <div className="mb-24">
            <h2 className="text-3xl font-bold text-white text-center mb-12">📱 Compact Sizes</h2>
            <div className="flex flex-wrap justify-center gap-8">
              
              <div className="text-center">
                <h4 className="text-lg font-semibold text-white mb-4">Mini Card</h4>
                <CosmicNebulaMastercard 
                  cardholderName="J. DOE"
                  logoText={{ topText: "MINI", bottomText: "FLUX" }}
                  width="200px"
                  height="125px"
                />
              </div>

              <div className="text-center">
                <h4 className="text-lg font-semibold text-white mb-4">Standard</h4>
                <CosmicNebulaMastercard 
                  cardholderName="STANDARD USER"
                  logoText={{ topText: "COSMIC", bottomText: "CARD" }}
                  width="300px"
                  height="190px"
                />
              </div>

              <div className="text-center">
                <h4 className="text-lg font-semibold text-white mb-4">Large Display</h4>
                <CosmicNebulaMastercard 
                  cardholderName="ENTERPRISE MEMBER"
                  logoText={{ topText: "ENTERPRISE", bottomText: "COSMIC" }}
                  width="500px"
                  height="315px"
                />
              </div>
            </div>
          </div>

          {/* Features Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24">
            
            {/* Technical Features */}
            <div className="bg-white/5 backdrop-blur rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">⚡ Technical Features</h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-blue-500 mr-3 mt-1.5 flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-white">3D Mouse Tracking</p>
                    <p className="text-white/60 text-sm">Real-time 3D rotation based on cursor position</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-purple-500 mr-3 mt-1.5 flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-white">Dynamic Animations</p>
                    <p className="text-white/60 text-sm">Particle systems, aurora effects, and holographic shifting</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-3 mt-1.5 flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-white">Cosmic Star Fields</p>
                    <p className="text-white/60 text-sm">Multi-layered star animations with twinkling effects</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-yellow-500 mr-3 mt-1.5 flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-white">Responsive Design</p>
                    <p className="text-white/60 text-sm">Adapts perfectly to different screen sizes and containers</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-red-500 mr-3 mt-1.5 flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-white">Theme Customization</p>
                    <p className="text-white/60 text-sm">Full control over colors, branding, and visual effects</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Use Cases */}
            <div className="bg-white/5 backdrop-blur rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">🎯 Perfect Use Cases</h3>
              
              <div className="grid grid-cols-1 gap-4">
                <div className="p-4 bg-white/10 rounded-lg">
                  <h4 className="font-semibold text-white mb-2">💳 Premium Credit Cards</h4>
                  <p className="text-white/60 text-sm">High-end financial products and luxury banking interfaces</p>
                </div>
                
                <div className="p-4 bg-white/10 rounded-lg">
                  <h4 className="font-semibold text-white mb-2">🏆 Membership Cards</h4>
                  <p className="text-white/60 text-sm">VIP memberships, club cards, and exclusive access programs</p>
                </div>
                
                <div className="p-4 bg-white/10 rounded-lg">
                  <h4 className="font-semibold text-white mb-2">🎮 Gaming UIs</h4>
                  <p className="text-white/60 text-sm">Futuristic game interfaces and sci-fi themed applications</p>
                </div>
                
                <div className="p-4 bg-white/10 rounded-lg">
                  <h4 className="font-semibold text-white mb-2">🌟 Portfolio Showcases</h4>
                  <p className="text-white/60 text-sm">Creative portfolios and premium service presentations</p>
                </div>
              </div>
            </div>
          </div>

          {/* Integration Code Example */}
          <div className="bg-gray-900/50 backdrop-blur rounded-2xl p-8 mb-24">
            <h3 className="text-2xl font-bold text-white mb-6">👨‍💻 Integration Example</h3>
            
            <div className="bg-black/50 rounded-lg p-6 overflow-x-auto">
              <pre className="text-green-400 text-sm">
                <code>{`import CosmicNebulaMastercard from '@/components/ui/cursor-wander-card'

// Basic Usage
<CosmicNebulaMastercard 
  cardholderName="JOHN DOE"
  logoText={{ topText: "BRAND", bottomText: "NAME" }}
/>

// Hudson Club Themed
<CosmicNebulaMastercard 
  cardholderName="HUDSON CLUB MEMBER"
  logoText={{ topText: "HUDSON", bottomText: "CLUB" }}
  theme={{
    primaryColor: "#82764d",
    secondaryColor: "#1e1d1c", 
    glowColor: "rgba(130, 118, 77, 0.8)"
  }}
  width="400px"
  height="250px"
/>

// Custom Size & Colors
<CosmicNebulaMastercard 
  cardholderName="VIP MEMBER"
  theme={{
    primaryColor: "#10b981",
    secondaryColor: "#059669",
    glowColor: "rgba(16, 185, 129, 0.8)"
  }}
  width="350px"
  height="220px"
  className="mx-auto"
/>`}</code>
              </pre>
            </div>
            
            <div className="mt-6 p-4 bg-blue-500/20 rounded-lg border border-blue-500/30">
              <h4 className="font-semibold text-blue-300 mb-2">💡 Pro Tip</h4>
              <p className="text-blue-100 text-sm">
                The card automatically handles mouse tracking and animations. For best performance, 
                avoid placing multiple large cards on the same page simultaneously.
              </p>
            </div>
          </div>

          {/* Hudson Club Integration */}
          <div className="text-center bg-gradient-to-r from-blue-900/20 to-purple-900/20 rounded-3xl p-12 mb-24">
            <h2 className="text-3xl font-bold text-white mb-4">🏠 Perfect for Hudson Club</h2>
            <p className="text-white/80 text-lg mb-8">
              This cosmic card component would be perfect for Hudson Club's premium member portal, 
              exclusive service cards, or luxury home management dashboards.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <div className="bg-white/10 rounded-lg p-6 text-center backdrop-blur">
                <h4 className="font-semibold text-white mb-2">Member Cards</h4>
                <p className="text-white/60 text-sm">Exclusive membership tiers with cosmic branding</p>
              </div>
              
              <div className="bg-white/10 rounded-lg p-6 text-center backdrop-blur">
                <h4 className="font-semibold text-white mb-2">Service Packages</h4>
                <p className="text-white/60 text-sm">Premium home management service displays</p>
              </div>
              
              <div className="bg-white/10 rounded-lg p-6 text-center backdrop-blur">
                <h4 className="font-semibold text-white mb-2">VIP Dashboard</h4>
                <p className="text-white/60 text-sm">Interactive member portal interfaces</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CursorWanderCardDemoPage 